/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Edit, X, UserPlus, MessageCircle } from 'lucide-react';
import { messagesAPI, usersAPI } from '../../utils/api';
import { useSocket } from '../../context/SocketContext';
import { getAvatarUrl, formatTime } from '../../utils/helpers';

// PRIVACY: This component NEVER calls usersAPI.getAll().
// It only shows:
//   1) People you've actually messaged (via /messages/conversations/list)
//   2) Search results when the user explicitly types 2+ characters
// A brand-new account with zero messages MUST see an empty list here.

export default function ConversationList({ activeUserId, onSelect }) {
  const [conversations, setConversations] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [query,         setQuery]         = useState('');
  const [loading,       setLoading]       = useState(true);
  const [searching,     setSearching]     = useState(false);
  const [showSearch,    setShowSearch]    = useState(false);
  const { isOnline, notifications } = useSocket();
  const navigate = useNavigate();
  const searchTimer = useRef(null);

  const loadConversations = async () => {
    try {
      const { data } = await messagesAPI.getConversations();
      setConversations(Array.isArray(data) ? data : []);
    } catch {
      setConversations([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadConversations(); }, []);
  useEffect(() => { if (notifications.length > 0) loadConversations(); }, [notifications.length]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (!query.trim() || query.trim().length < 2) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await usersAPI.search(query.trim());
        setSearchResults(Array.isArray(data) ? data : []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [query]);

  const handleSelectUser = (userId) => {
    setQuery(''); setSearchResults([]); setShowSearch(false);
    navigate(`/chat/${userId}`);
    if (onSelect) onSelect(userId);
  };

  const getUnread = (otherUserId) =>
    notifications.filter(n => n.type==='dm' && n.senderId===otherUserId).length;

  const isSearchMode = query.trim().length >= 2;
  const displayList  = isSearchMode ? searchResults : conversations;

  return (
    <div className="conversation-list">

      <div className="conv-list-header">
        <h2>Messages</h2>
        <div style={{display:'flex',gap:6}}>
          <button className="btn-icon" title="Search" onClick={()=>setShowSearch(v=>!v)}
            style={{color:showSearch?'var(--primary-light)':undefined}}>
            <Search size={17}/>
          </button>
          <button className="btn-icon" title="New Message" onClick={()=>setShowSearch(true)}>
            <Edit size={17}/>
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="conv-search">
          <Search size={14}/>
          <input
            autoFocus
            placeholder="Search people by name or @username..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query && (
            <button className="btn-icon" style={{width:24,height:24}}
              onClick={() => { setQuery(''); setSearchResults([]); }}>
              <X size={13}/>
            </button>
          )}
        </div>
      )}

      {showSearch && !query && (
        <div style={{padding:'10px 14px',fontSize:'.78rem',color:'var(--text-500)',display:'flex',alignItems:'center',gap:6}}>
          <UserPlus size={14}/>
          Type at least 2 letters to find someone new
        </div>
      )}

      <div className="conv-body">
        {loading ? (
          Array(4).fill(0).map((_,i) => (
            <div key={i} className="conv-item">
              <div className="skeleton" style={{width:44,height:44,borderRadius:'50%',flexShrink:0}}/>
              <div style={{flex:1}}>
                <div className="skeleton" style={{width:'60%',height:12,marginBottom:6}}/>
                <div className="skeleton" style={{width:'80%',height:10}}/>
              </div>
            </div>
          ))
        ) : searching ? (
          <div style={{padding:'20px',textAlign:'center'}}>
            <div className="loader" style={{width:20,height:20,borderWidth:2,margin:'0 auto'}}/>
          </div>
        ) : displayList.length === 0 ? (
          <div style={{padding:'40px 20px',textAlign:'center',color:'var(--text-500)'}}>
            {isSearchMode ? (
              <>
                <Search size={32} style={{opacity:.3,margin:'0 auto 10px'}}/>
                <p style={{fontSize:'.85rem',fontWeight:500}}>No users found</p>
                <p style={{fontSize:'.78rem',marginTop:4}}>Try a different name or username</p>
              </>
            ) : (
              <>
                <MessageCircle size={36} style={{opacity:.3,margin:'0 auto 12px'}}/>
                <p style={{fontSize:'.9rem',fontWeight:600,color:'var(--text-300)'}}>No conversations yet</p>
                <p style={{fontSize:'.8rem',marginTop:6,lineHeight:1.5}}>
                  For your privacy, Nexa only shows people<br/>you've actually messaged.
                </p>
                <button className="btn btn-primary btn-sm" style={{marginTop:16}}
                  onClick={()=>setShowSearch(true)}>
                  <Search size={13}/> Find someone to message
                </button>
              </>
            )}
          </div>
        ) : displayList.map(item => {
          const isConv    = item.otherUser !== undefined;
          const otherUser = isConv ? item.otherUser : item;
          const uid       = otherUser?._id || otherUser?.id;
          const online    = isOnline(uid);
          const unread    = isConv ? getUnread(uid) : 0;
          const lastMsg   = isConv ? item.lastMessage : null;

          return (
            <div
              key={uid}
              className={`conv-item ${activeUserId===uid?'active':''}`}
              onClick={() => handleSelectUser(uid)}
            >
              <div style={{position:'relative',flexShrink:0}}>
                <img src={getAvatarUrl(otherUser)} alt="" className="avatar" style={{width:44,height:44}}/>
                <span className={`status-dot ${online?'online':'offline'}`} style={{position:'absolute',bottom:0,right:0}}/>
              </div>
              <div className="conv-info">
                <div className="conv-top">
                  <span className="conv-name">{otherUser?.displayName}</span>
                  {lastMsg && <span className="conv-time">{formatTime(lastMsg.createdAt)}</span>}
                </div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <p className="conv-preview">
                    {!isConv
                      ? <span style={{color:'var(--primary-light)',fontWeight:500}}>@{otherUser?.username}</span>
                      : lastMsg?.deleted
                        ? <em>Message deleted</em>
                        : lastMsg?.type==='voice' ? '🎙 Voice note'
                        : lastMsg?.type==='image' ? '🖼 Image'
                        : lastMsg?.type==='file'  ? '📎 File'
                        : lastMsg?.content?.slice(0,42) || 'Start a conversation'}
                  </p>
                  {unread > 0 && <span className="conv-unread">{unread > 9 ? '9+' : unread}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}